import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import CustomPaymentMethodsList from '../../commonComponents/CustomPaymentMethodsList';
import {
  updateBookingFeeRatio,
  updateCurrentUser,
  updateCurrentUserPayment,
  updateNetTerms,
} from '../../../actions/userActions';
import {
  formatBookingFeeDisplay,
  isValidBookingFeeInput,
  parseBookingFee,
} from '../../../utils/BookingFeeUtils';

import '../../../themes/ProfilePaymentDNT.scss';
import { TabViewContainer } from '../../commonComponents/TabViewContainer';
import { getChangeChargeTimelineValue } from '../../../growthbook';

export default function ProfilePaymentDNT({
  accounts,
  isPaymentSelected,
  onPreApprovedChanged,
  preApproved,
  onReceiptEnabledChanged,
  receiptEnabled,
  preApproveUser,
}) {
  const currentUser = useSelector((state) => state.users.currentUser);
  const isPayoutUser = useSelector((state) => state.users.isPayoutUser);

  const [editProfile, setEditProfile] = useState(false);

  const [rateLimit, setRateLimit] = useState(currentUser.rate_limit);
  const [rateLimitDA, setRateLimitDA] = useState(currentUser?.rate_limit_da);
  const [rateLimitDN, setRateLimitDN] = useState(currentUser?.rate_limit_dn);
  const [rateLimitFD, setRateLimitFD] = useState(currentUser?.rate_limit_fd);

  const [creditLimit, setCreditLimit] = useState(currentUser.credit_limit);
  const [bookingFee, setBookingFee] = useState(
    typeof currentUser.booking_fee_ratio === 'number'
      ? Number((currentUser.booking_fee_ratio * 100).toFixed(2))
      : '',
  );
  const [stripeEmailValue, setStripeEmail] = useState(currentUser.stripe_billing_email || '');

  const dispatch = useDispatch();
  const history = useHistory();
  const { profileType, id } = useParams();

  /**
   * @param title
   * @param {string | undefined} info
   * @param {'number' | 'text'} type
   * @param {function(string)} onDidChange
   * @param {boolean} [disabled]
   * @param {string} [hint]
   * @param {'decimal' | 'numeric' | 'text'} [inputMode]
   * @returns {React.JSX.Element}
   */
  const renderLine = ({ title, info, type, onDidChange, disabled, hint, inputMode }) => {
    const isIntegerInput = type === 'number' && !inputMode;
    const inputValue =
      isIntegerInput && typeof info === 'string'
        ? (parseInt(info.replace(/\D/g, ''), 10) ?? 0)
        : info;

    return (
      <div
        className="container"
        style={{
          width: '100%',
          borderBottom: '1px solid #efefef',
          padding: 5,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p className="col-8 semi-bold">{title}</p>
          {editProfile ? (
            <div className="col-4" style={{ padding: 0 }}>
              <input
                className={type === 'number' ? 'form-control disabled-arrow-input' : 'form-control'}
                value={inputValue}
                type={type || 'text'}
                inputMode={inputMode}
                disabled={disabled}
                onChange={(e) => onDidChange(e.target.value)}
              />
            </div>
          ) : (
            <p className="col-4" style={{ textAlign: 'right' }}>
              {info}
            </p>
          )}
        </div>
        {hint && editProfile ? (
          <p
            style={{ marginTop: 6, marginBottom: 0, fontSize: 12, color: '#6c757d' }}
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  };

  const isBookingFeeFromDso = currentUser?.booking_fee_ratio_source === 'dso';

  const bookingFeeIsEmpty = String(bookingFee).trim() === '';
  const isBookingFeeInputInvalid =
    !isBookingFeeFromDso && !bookingFeeIsEmpty && !isValidBookingFeeInput(bookingFee);
  const bookingFeeDisplay = formatBookingFeeDisplay(
    typeof currentUser?.booking_fee_ratio === 'number' ? currentUser.booking_fee_ratio * 100 : null,
  );

  const renderDentistPaymentInfo = () => (
    <div style={{ width: '100%' }}>
      <p
        className="card-title text-center"
        style={{
          marginTop: 30,
        }}
      >
        <b>Dental Hygienist limit:</b>
      </p>

      {renderLine({
        type: 'number',
        onDidChange: (val) => setRateLimit(val),
        title: 'Rate limit for RDH for all child offices',
        info: editProfile
          ? rateLimit
          : currentUser.rate_limit
            ? `$ ${currentUser.rate_limit}`
            : '-',
      })}

      <div
        className="container"
        style={{
          width: '100%',
          borderBottom: '1px solid #efefef',
          padding: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <p className="col-8 semi-bold">Rate limit for RDH (setting from master DSO office)</p>
        <p className="col-4" style={{ textAlign: 'right' }}>
          {currentUser.rate_limit_descend ? `$ ${currentUser.rate_limit_descend}` : '-'}
        </p>
      </div>
      <p
        className="card-title text-center"
        style={{
          marginTop: 30,
        }}
      >
        <b>Dental Assistant limit:</b>
      </p>
      {renderLine({
        type: 'number',
        onDidChange: (val) => setRateLimitDA(val),
        title: 'Rate limit for DA for all child offices',
        info: editProfile
          ? rateLimitDA
          : currentUser.rate_limit_da
            ? `$ ${currentUser.rate_limit_da}`
            : '-',
      })}

      <div
        className="container"
        style={{
          width: '100%',
          borderBottom: '1px solid #efefef',
          padding: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <p className="col-8 semi-bold">Rate limit for DA (setting from master DSO office)</p>
        <p className="col-4" style={{ textAlign: 'right' }}>
          {currentUser.rate_limit_da_descend ? `$ ${currentUser.rate_limit_da_descend}` : '-'}
        </p>
      </div>
      <p
        className="card-title text-center"
        style={{
          marginTop: 30,
        }}
      >
        <b>Associate Dentist limit:</b>
      </p>
      {renderLine({
        type: 'number',
        onDidChange: (val) => setRateLimitDN(val),
        title: 'Rate limit for DN for all child offices',
        info: editProfile
          ? rateLimitDN
          : currentUser.rate_limit_dn
            ? `$ ${currentUser.rate_limit_dn}`
            : '-',
      })}
      <div
        className="container"
        style={{
          width: '100%',
          borderBottom: '1px solid #efefef',
          padding: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <p className="col-8 semi-bold">Rate limit for DN (setting from master DSO office)</p>
        <p className="col-4" style={{ textAlign: 'right' }}>
          {currentUser.rate_limit_dn_descend ? `$ ${currentUser.rate_limit_dn_descend}` : '-'}
        </p>
      </div>

      <p
        className="card-title text-center"
        style={{
          marginTop: 30,
        }}
      >
        <b>Front Desk limit:</b>
      </p>
      {renderLine({
        type: 'number',
        onDidChange: (val) => setRateLimitFD(val),
        title: 'Rate limit for FD for all child offices',
        info: editProfile
          ? rateLimitFD
          : currentUser.rate_limit_fd
            ? `$ ${currentUser.rate_limit_fd}`
            : '-',
      })}
      <div
        className="container"
        style={{
          width: '100%',
          borderBottom: '1px solid #efefef',
          padding: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <p className="col-8 semi-bold">Rate limit for FD (setting from master DSO office)</p>
        <p className="col-4" style={{ textAlign: 'right' }}>
          {currentUser.rate_limit_fd_descend ? `$ ${currentUser.rate_limit_fd_descend}` : '-'}
        </p>
      </div>

      <p
        className="card-title text-center"
        style={{
          marginTop: 30,
        }}
      >
        <b>Payments Settings:</b>
      </p>
      {renderLine({
        type: 'number',
        onDidChange: (val) => setCreditLimit(val),
        title: 'Credit Limit',
        info: editProfile
          ? creditLimit
          : `$ 
    ${new Intl.NumberFormat('en-US').format(currentUser.credit_limit)}` || '-',
      })}

      {renderLine({
        type: 'text',
        inputMode: 'decimal',
        onDidChange: (val) => setBookingFee(val),
        title: 'Booking fee rate',
        info: editProfile ? bookingFee : bookingFeeDisplay,
        disabled: isBookingFeeFromDso,
        hint: isBookingFeeFromDso
          ? "This office's RDH Booking Fee rate is assigned on a DSO-level and cannot be changed."
          : undefined,
      })}

      {renderLine({
        type: 'text',
        onDidChange: (val) => setStripeEmail(val),
        title: 'Billing Email',
        info: editProfile ? stripeEmailValue : stripeEmailValue || '-',
      })}
    </div>
  );

  const resetFormState = () => {
    setRateLimit(currentUser.rate_limit);
    setRateLimitDA(currentUser?.rate_limit_da);
    setRateLimitDN(currentUser?.rate_limit_dn);
    setRateLimitFD(currentUser?.rate_limit_fd);
    setCreditLimit(currentUser.credit_limit);
    setBookingFee(
      typeof currentUser.booking_fee_ratio === 'number'
        ? Number((currentUser.booking_fee_ratio * 100).toFixed(2))
        : '',
    );
    setStripeEmail(currentUser.stripe_billing_email || '');
  };

  const enableEdit = () => {
    resetFormState();
    setEditProfile(true);
  };

  const cancelEdit = () => {
    resetFormState();
    setEditProfile(false);
  };

  const saveEditProfile = () => {
    const newBillingEmail =
      stripeEmailValue !== (currentUser?.stripe_billing_email || '')
        ? { stripe_billing_email: stripeEmailValue }
        : {};
    const newCreditLimit =
      Number(creditLimit) !== Number(currentUser?.credit_limit)
        ? { credit_limit: Number(creditLimit) }
        : {};
    const newRateLimit =
      Number(rateLimit) !== Number(currentUser?.rate_limit) && rateLimit
        ? { rate_limit: Number(rateLimit) }
        : {};
    const newRateLimitDA =
      Number(rateLimitDA) !== Number(currentUser?.rate_limit_da) && rateLimitDA
        ? { rate_limit_da: Number(rateLimitDA) }
        : {};
    const newRateLimitDN =
      Number(rateLimitDN) !== Number(currentUser?.rate_limit_dn) && rateLimitDN
        ? { rate_limit_dn: Number(rateLimitDN) }
        : {};
    const newRateLimitFD =
      Number(rateLimitFD) !== Number(currentUser?.rate_limit_fd) && rateLimitFD
        ? { rate_limit_fd: Number(rateLimitFD) }
        : {};

    const paymentUpdates = {
      ...newBillingEmail,
      ...newCreditLimit,
      ...newRateLimit,
      ...newRateLimitDA,
      ...newRateLimitDN,
      ...newRateLimitFD,
    };

    if (Object.keys(paymentUpdates).length > 0) {
      dispatch(updateCurrentUserPayment(paymentUpdates));
    }

    const currentBookingFeeRatio = currentUser?.booking_fee_ratio;
    const currentBookingFeePercent =
      typeof currentBookingFeeRatio === 'number' ? (currentBookingFeeRatio * 100).toFixed(2) : null;
    const draftBookingFeePercent =
      String(bookingFee).trim() === '' ? null : Number(bookingFee).toFixed(2);
    const bookingFeeChanged =
      !isBookingFeeFromDso
      && draftBookingFeePercent !== null
      && draftBookingFeePercent !== currentBookingFeePercent;
    if (bookingFeeChanged) {
      dispatch(updateBookingFeeRatio(parseBookingFee(bookingFee)));
    }

    setEditProfile(false);
  };

  return (
    <TabViewContainer
      style={{
        maxWidth: 1200,
        width: '90%',
      }}
      title="Payment Methods"
      variant="card"
      TitleTrailing={
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            className="btn btn-info"
            type="button"
            onClick={() => history.push(`/user/${profileType}/${id}/achAuthorization`)}
          >
            ACH Authorization
          </button>
        </div>
      }
    >
      <div
        className="card-body profile-payment-dnt-body"
        style={{ width: '100%', marginTop: 0, padding: 0 }}
      >
        <CustomPaymentMethodsList accounts={accounts || []} isPaymentSelected={isPaymentSelected} />

        <div
          className="card border-0"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            marginTop: '25px',
            width: '80%',
          }}
        >
          <h3 className="card-title text-center">
            <b>Pre Approve</b>
          </h3>
          <div
            style={{
              margin: 25,
              width: '60%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <p className="card-text" style={{ width: 200, marginRight: '12px' }}>
              Is Pre Approved user
            </p>

            <select
              className="custom-select custom-select-lg mb-3"
              value={preApproved}
              onChange={(val) => onPreApprovedChanged(val.target.value)}
              disabled={!preApproveUser}
            >
              <option className="dropdown-item" value>
                Yes
              </option>
              <option className="dropdown-item" value={false}>
                No
              </option>
            </select>
          </div>

          {preApproved && preApproved !== 'false' && (
            <div
              style={{
                margin: 25,
                width: '60%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <p className="card-text" style={{ width: 200, marginRight: '12px' }}>
                Net Terms
              </p>

              <select
                className="custom-select custom-select-lg mb-3"
                value={currentUser.netTerms || 14}
                onChange={(val) => dispatch(updateNetTerms(Number(val.target.value)))}
              >
                <option className="dropdown-item" value={14}>
                  14 days
                </option>
                <option className="dropdown-item" value={21}>
                  21 days
                </option>
                <option className="dropdown-item" value={30}>
                  30 days
                </option>
              </select>
            </div>
          )}

          <div
            style={{
              margin: 25,
              width: '60%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <p className="card-text" style={{ width: 200, marginRight: '12px' }}>
              Is Receipt enabled
            </p>

            <select
              className="custom-select custom-select-lg mb-3"
              value={receiptEnabled}
              onChange={(val) => onReceiptEnabledChanged(val.target.value)}
            >
              <option className="dropdown-item" value>
                Yes
              </option>
              <option className="dropdown-item" value={false}>
                No
              </option>
            </select>
          </div>

          <h3 className="card-title text-center">
            <b>Charge Timeline</b>
          </h3>

          <p className="card-text" style={{ textAlign: 'center', marginTop: 20 }}>
            The field below controls when offices that pay with the following payment methods will
            get charged for professionals pay
            <br />
            if booked in advance of the selected time: credit card, debit card, ACH / Bank Transfer.
          </p>

          {getChangeChargeTimelineValue() ? (
            <div
              style={{
                margin: 25,
                width: '60%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <p className="card-text" style={{ width: 120 }}>
                Charge Timeline
              </p>

              <select
                className="custom-select custom-select-lg mb-3"
                value={currentUser.mdbChargeTimelineDays || 28}
                onChange={(val) =>
                  dispatch(
                    updateCurrentUser({
                      mdbChargeTimelineDays: val.target.value,
                    }),
                  )
                }
                style={{ width: 120 }}
              >
                <option className="dropdown-item" value={28}>
                  28 days
                </option>
                <option className="dropdown-item" value={7}>
                  7 days
                </option>
              </select>
            </div>
          ) : (
            <div />
          )}

          <h3 className="card-title text-center">
            <b>Payment settings</b>
          </h3>
          {renderDentistPaymentInfo()}

          {editProfile && isBookingFeeInputInvalid ? (
            <p style={{ color: 'red' }}>
              Booking Fee must be a number between 0.00 and 100.00 (up to 2 decimals).
            </p>
          ) : null}

          <div className="actions">
            {!editProfile && isPayoutUser ? (
              <button className="btn btn-info" type="button" onClick={() => enableEdit()}>
                Edit
              </button>
            ) : (
              ''
            )}

            {!editProfile ? (
              ''
            ) : (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => cancelEdit()}
              >
                Cancel
              </button>
            )}

            {!editProfile ? (
              ''
            ) : (
              <button
                className="btn btn-success"
                type="button"
                onClick={() => saveEditProfile()}
                disabled={isBookingFeeInputInvalid}
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
      <div style={{ height: 20 }} />
    </TabViewContainer>
  );
}
